import { useEffect, useState } from 'react';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { cadenceToDays, type Cadence } from '@traaker/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { brandQueriesApi } from '../../services/brand-queries';
import { conversationsApi } from '../prompt-studio/conversations/conversationsApi';
import { formatNumber } from '../../i18n/format';

const POLL_MS = 60_000;

/** The monthly PROJECTION of the saved configuration — the Prompt Studio equation's result. */
interface Projection {
  allowance: number;
  /** perLaunch × launchesInPeriod over the active prompts / conversations. */
  sustainableNeed: number;
  perLaunch: number;
  launchesInPeriod?: number;
  cadence?: Cadence;
}

/**
 * Header bars beside the credit chip (non-agency): the active brand's monthly
 * PROJECTION — what the saved prompts × engines × cadence will consume per month
 * (`sustainableNeed`) against the allowance, the same figure as the Prompt Studio
 * footer equation — for prompts and, only when the tier has some, conversations.
 * Consumption so far lives in Settings → Organisation. Allowances are per brand
 * today. Agency is credit-metered (allowance -1), so nothing renders there.
 */
export default function AllowanceUsageBars() {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const { activeBrand } = useBrand();
  const [prompts, setPrompts] = useState<Projection | null>(null);
  const [conversations, setConversations] = useState<Projection | null>(null);

  const isAgency = tenant?.tier === 'agency';
  const brandId = activeBrand?.brandId;

  useEffect(() => {
    setPrompts(null);
    setConversations(null);
    if (!brandId || isAgency) return;
    let alive = true;
    const load = async () => {
      try {
        const a = await brandQueriesApi.allowances(brandId);
        if (!alive) return;
        setPrompts(a.promptRequests);
        if (a.conversationRuns.allowance > 0) {
          const c = await conversationsApi.load(brandId);
          if (alive) setConversations({ ...c.figures, cadence: c.cadence ?? undefined });
        } else {
          setConversations(null);
        }
      } catch {
        // a failed read hides the bars, never blocks the header
      }
    };
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [brandId, isAgency]);

  if (!prompts) return null;
  const brand = activeBrand?.name ?? '';
  const cadenceText = (c?: Cadence): string | undefined => {
    if (!c || c === 'off') return undefined;
    if (c.unit === 'months') return t('promptStudio.pop.cadPlainMonthly');
    const d = cadenceToDays(c);
    return d === 1 ? t('allowanceBar.cadenceDaily') : t('promptStudio.pop.cadPlain', { d });
  };
  const tooltip = (p: Projection, unitKey: 'requests' | 'runs') => {
    const cad = cadenceText(p.cadence);
    const base = { brand, n: formatNumber(p.sustainableNeed), cap: formatNumber(p.allowance) };
    return cad && p.launchesInPeriod
      ? t(`allowanceBar.${unitKey}Projection`, { ...base, perLaunch: formatNumber(p.perLaunch), cadence: cad })
      : t(`allowanceBar.${unitKey}ProjectionPlain`, base);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 1 }}>
      <ProjectionBar label={t('allowanceBar.prompts')} p={prompts} tooltip={tooltip(prompts, 'requests')} />
      {conversations && (
        <ProjectionBar label={t('allowanceBar.conversations')} p={conversations} tooltip={tooltip(conversations, 'runs')} />
      )}
    </Box>
  );
}

function ProjectionBar({ label, p, tooltip }: { label: string; p: Projection; tooltip: string }) {
  if (!(p.allowance > 0)) return null; // 0 = not in the plan, -1 = unlimited: no bar
  const ratio = p.sustainableNeed / p.allowance;
  const color = ratio > 1 ? 'error' : ratio >= 0.8 ? 'warning' : 'primary';
  // Compact: label + bar only; « x / total » and the projection detail on hover.
  return (
    <Tooltip
      title={
        <>
          <Box component="span" sx={{ display: 'block', fontWeight: 700 }}>
            {formatNumber(p.sustainableNeed)} / {formatNumber(p.allowance)}
          </Box>
          {tooltip}
        </>
      }
    >
      <Box sx={{ width: 80 }} data-tour="allowance-usage">
        <Typography variant="caption" color={ratio > 1 ? 'error.main' : 'text.secondary'} fontWeight={600}>
          {label}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, ratio * 100)}
          color={color}
          sx={{ height: 4, borderRadius: 2 }}
        />
      </Box>
    </Tooltip>
  );
}
